import { useRef, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  LinkInBioTemplateFields,
  type LinkInBioTemplateFieldsHandle,
} from "./LinkInBioTemplateFields";
import {
  SellAProductTemplateFields,
  type SellAProductTemplateFieldsHandle,
} from "./SellAProductTemplateFields";
import type { Automation, AutomationTemplate } from "./types";

export function TemplateTab({
  automationId,
  template,
  formKey,
  onRefreshed,
}: {
  automationId: string;
  template: AutomationTemplate;
  formKey: string;
  onRefreshed: (automation: Automation) => void;
}) {
  const linkInBioRef = useRef<LinkInBioTemplateFieldsHandle>(null);
  const sellAProductRef = useRef<SellAProductTemplateFieldsHandle>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const handleRefresh = async () => {
    const isLinkInBio = template.type === "link-in-bio";
    const handle = isLinkInBio ? linkInBioRef.current : sellAProductRef.current;
    if (!handle || !handle.isValid()) {
      alert(
        isLinkInBio
          ? "Fan capture label is required"
          : "Select a product or enter a name and price"
      );
      return;
    }

    setIsRefreshing(true);
    try {
      const payload = handle.getPayload();
      const body = isLinkInBio
        ? { linkInBio: payload }
        : { sellAProduct: payload };
      const response = await fetch(
        `/api/automations/${automationId}/refresh-template`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      );
      const data = await response.json();
      if (!response.ok || !data.automation) {
        throw new Error(data.error || "Failed to refresh template");
      }
      onRefreshed(data.automation);
    } catch (error) {
      console.error("Failed to refresh template:", error);
      alert(
        error instanceof Error ? error.message : "Failed to refresh template"
      );
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="flex-1 min-h-0 overflow-y-auto">
      <div className="mx-auto w-full max-w-lg space-y-6 p-6">
        {template.type === "link-in-bio" ? (
          <LinkInBioTemplateFields
            key={formKey}
            ref={linkInBioRef}
            context={template.context}
            disabled={isRefreshing}
            onDirtyChange={setHasChanges}
          />
        ) : (
          <SellAProductTemplateFields
            key={formKey}
            ref={sellAProductRef}
            context={template.context}
            disabled={isRefreshing}
            onDirtyChange={setHasChanges}
          />
        )}
        <Button
          type="button"
          className="w-full"
          data-testid="automation-template-refresh"
          onClick={() => void handleRefresh()}
          disabled={isRefreshing || !hasChanges}
        >
          {isRefreshing ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Refreshing Flow...
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4" />
              Refresh Flow
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
